import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {addMonths, format, getDaysInMonth, parseISO, isValid} from "date-fns";
import {useTranslation} from "react-i18next";
import {CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X} from "lucide-react";
import {useDateFnsLocale} from "../../i18n/dateLocale";

// All values are "YYYY-MM-DD" strings (same contract as <input type="date">),
// so bounds can be compared lexicographically.
const pad = (n, len = 2) => String(n).padStart(len, "0");
const toIso = (y, m, d) => `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
const todayIso = () => format(new Date(), "yyyy-MM-dd");
const parseValue = (value) => {
    if (!value) return null;
    const date = parseISO(String(value).slice(0, 10));
    return isValid(date) ? date : null;
};
const outOfBounds = (iso, min, max) => (min && iso < min) || (max && iso > max);
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const SEGMENTS = [
    {key: "d", len: 2, maxFirst: 3},
    {key: "m", len: 2, maxFirst: 1},
    {key: "y", len: 4},
];

const segmentsFrom = (date) => date
    ? {d: pad(date.getDate()), m: pad(date.getMonth() + 1), y: String(date.getFullYear())}
    : {d: "", m: "", y: ""};

// Typed DD / MM / YYYY field at the top of the calendar
export const SegmentedField = ({value, min, max, onCommit, onEnter = () => {}, autoFocus, compact = false, "aria-label": ariaLabel}) => {
    const {t} = useTranslation();
    const [seg, setSeg] = useState(() => segmentsFrom(parseValue(value)));
    const refs = useRef([]);

    useEffect(() => setSeg(segmentsFrom(parseValue(value))), [value]);
    useEffect(() => {
        if (autoFocus) refs.current[0]?.focus({preventScroll: true});
    }, [autoFocus]);

    const tryCommit = (next) => {
        if (next.d.length < 1 || next.m.length < 1 || next.y.length !== 4) return;
        const y = +next.y, m = +next.m, d = +next.d;
        if (m < 1 || m > 12 || d < 1 || d > getDaysInMonth(new Date(y, m - 1))) return;
        const iso = toIso(y, m, d);
        if (!outOfBounds(iso, min, max) && iso !== value) onCommit(iso);
    };

    const update = (next, focusIndex) => {
        setSeg(next);
        tryCommit(next);
        if (focusIndex != null) refs.current[focusIndex]?.focus();
    };

    const handleChange = (i) => (e) => {
        const {key, len, maxFirst} = SEGMENTS[i];
        const digits = e.target.value.replace(/\D/g, "").slice(-len);
        const done = digits.length === len || (maxFirst != null && digits.length === 1 && +digits > maxFirst);
        update({...seg, [key]: done && len === 2 ? pad(digits) : digits}, done && i < 2 ? i + 1 : null);
    };

    const handleKeyDown = (i) => (e) => {
        const {key} = SEGMENTS[i];
        if (e.key === "Enter") { e.preventDefault(); onEnter(); return; }
        if (e.key === "Backspace" && !seg[key] && i > 0) { e.preventDefault(); refs.current[i - 1]?.focus(); return; }
        if (e.key === "ArrowLeft" && i > 0 && e.target.selectionStart === 0) { e.preventDefault(); refs.current[i - 1]?.focus(); return; }
        if (e.key === "ArrowRight" && i < 2 && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); refs.current[i + 1]?.focus(); return; }
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            const step = e.key === "ArrowUp" ? 1 : -1;
            const base = segmentsFrom(parseValue(value) || new Date());
            const current = seg[key] ? +seg[key] : +base[key];
            const [lo, hi] = key === "d" ? [1, 31] : key === "m" ? [1, 12] : [1900, 2100];
            const nextVal = current + step > hi ? lo : current + step < lo ? hi : current + step;
            update({...base, ...Object.fromEntries(Object.entries(seg).filter(([, v]) => v)), [key]: key === "y" ? String(nextVal) : pad(nextVal)});
        }
    };

    const placeholders = {d: t("datePicker.dd", "jj"), m: t("datePicker.mm", "mm"), y: t("datePicker.yyyy", "aaaa")};

    return (
        <div dir="ltr" role="group" aria-label={ariaLabel}
             className={`flex items-center h-10 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 focus-within:border-primary-light dark:focus-within:border-primary transition-colors text-gray-900 dark:text-gray-100 tabular-nums ${compact ? "gap-0.5 px-2 text-sm" : "gap-1 px-2.5 text-[15px]"}`}>
            {SEGMENTS.map(({key, len}, i) => (
                <React.Fragment key={key}>
                    {i > 0 && <span className="text-gray-400 px-0.5">/</span>}
                    <input
                        ref={(el) => (refs.current[i] = el)}
                        value={seg[key]}
                        onChange={handleChange(i)}
                        onKeyDown={handleKeyDown(i)}
                        onFocus={(e) => e.target.select()}
                        placeholder={placeholders[key]}
                        inputMode="numeric"
                        aria-label={placeholders[key]}
                        style={{width: `${len + 0.6}ch`}}
                        className="p-0 text-center bg-transparent border-0 outline-none focus:ring-0 rounded focus:bg-primary-lighter dark:focus:bg-primary/30 placeholder:text-gray-400"
                    />
                </React.Fragment>
            ))}
        </div>
    );
};

const navBtn = "p-1.5 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed";

const MONTH_WIDTH = 300;
const MONTH_GAP = 32;
// Popover width for `months` side-by-side grids (incl. p-4 padding)
export const calendarPopoverWidth = (months = 1) => months * MONTH_WIDTH + (months - 1) * MONTH_GAP + 32;

/**
 * Calendar panel: typed field + month header + day grid.
 * `onSelect(iso, {fromGrid})` fires for every valid pick.
 * Range mode: pass `range={[startIso, endIso]}` (highlighted band) and a
 * custom `field` to replace the single typed field.
 */
export const DateCalendar = ({value, min, max, onSelect, onEnter = () => {}, autoFocus = false, range, onHover, field, months = 1}) => {
    const locale = useDateFnsLocale();
    const selected = parseValue(value);
    const [view, setView] = useState(() => {
        const base = selected || parseValue(max && todayIso() > max ? max : min && todayIso() < min ? min : null) || new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });
    const [mode, setMode] = useState("days"); // days | months | years

    useEffect(() => {
        // Only scroll when the date isn't already in one of the visible months
        if (!selected) return;
        const target = new Date(selected.getFullYear(), selected.getMonth(), 1);
        setView((v) => (target >= v && target <= addMonths(v, months - 1) ? v : target));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const year = view.getFullYear();
    const month = view.getMonth();
    const today = todayIso();
    const minMonth = min ? min.slice(0, 7) : null;
    const maxMonth = max ? max.slice(0, 7) : null;
    const monthKey = (y, m) => `${pad(y, 4)}-${pad(m + 1)}`;
    const monthDisabled = (y, m) => (minMonth && monthKey(y, m) < minMonth) || (maxMonth && monthKey(y, m) > maxMonth);
    const yearDisabled = (y) => (min && String(y) < min.slice(0, 4)) || (max && String(y) > max.slice(0, 4));

    const step = (dir) => {
        if (mode === "days") setView(addMonths(view, dir));
        else if (mode === "months") setView(new Date(year + dir, month, 1));
        else setView(new Date(year + dir * 12, month, 1));
    };
    const yearsStart = year - (year % 12);
    const prev = addMonths(view, -1);
    const next = addMonths(view, mode === "days" ? months : 1);
    const prevDisabled = mode === "days" ? monthDisabled(prev.getFullYear(), prev.getMonth())
        : mode === "months" ? yearDisabled(year - 1) : yearDisabled(yearsStart - 1);
    const nextDisabled = mode === "days" ? monthDisabled(next.getFullYear(), next.getMonth())
        : mode === "months" ? yearDisabled(year + 1) : yearDisabled(yearsStart + 12);

    // Monday-first week
    const weekdays = Array.from({length: 7}, (_, i) => capitalize(format(new Date(2024, 0, 1 + i), "EEEEEE", {locale})));
    const shown = mode === "days" ? Array.from({length: months}, (_, i) => addMonths(view, i)) : [view];

    const modeLabel = mode === "months" ? String(year) : `${yearsStart} – ${yearsStart + 11}`;
    const modeToggle = (label) => (
        <button type="button" onClick={() => setMode(mode === "days" ? "months" : mode === "months" ? "years" : "days")}
                className="flex items-center gap-1 px-1.5 py-1 -ml-1.5 rounded-md text-[17px] font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
            {label}
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${mode !== "days" ? "rotate-180" : ""}`}/>
        </button>
    );
    const navButtons = (
        <div className="flex items-center gap-1">
            <button type="button" aria-label="Précédent" onClick={() => step(-1)} disabled={prevDisabled} className={navBtn}>
                <ChevronLeft className="w-5 h-5 rtl:rotate-180"/>
            </button>
            <button type="button" aria-label="Suivant" onClick={() => step(1)} disabled={nextDisabled} className={navBtn}>
                <ChevronRight className="w-5 h-5 rtl:rotate-180"/>
            </button>
        </div>
    );

    const renderDays = (monthDate) => {
        const y = monthDate.getFullYear();
        const m = monthDate.getMonth();
        const leading = (new Date(y, m, 1).getDay() + 6) % 7;
        const [start, end] = range || [value, value];
        return (
            <div className="grid grid-cols-7 gap-y-1 text-center" onMouseLeave={() => onHover?.(null)}>
                {weekdays.map((w) => (
                    <div key={w} className="h-8 flex items-center justify-center text-[13px] text-gray-500 dark:text-gray-400">{w}</div>
                ))}
                {Array.from({length: leading}, (_, i) => <div key={`b${i}`}/>)}
                {Array.from({length: getDaysInMonth(monthDate)}, (_, i) => i + 1).map((d) => {
                    const iso = toIso(y, m + 1, d);
                    const isSelected = iso === start || iso === end;
                    const disabled = outOfBounds(iso, min, max);
                    // Light band behind the days of a range; half-width at its ends
                    const band = range && start !== end && iso >= start && iso <= end
                        ? (iso === start ? "left-1/2 right-0" : iso === end ? "left-0 right-1/2" : "inset-x-0") : null;
                    return (
                        <div key={d} className="relative flex items-center justify-center">
                            {band && <span className={`absolute inset-y-0 ${band} bg-primary-lighter dark:bg-primary/20`}/>}
                            <button type="button" disabled={disabled} onClick={() => onSelect(iso, {fromGrid: true})}
                                    onMouseEnter={() => onHover?.(iso)}
                                    className={`relative w-9 h-9 rounded-full text-[15px] transition-colors ${
                                        isSelected ? "bg-primary text-white font-medium"
                                            : disabled ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                                : iso === today ? "font-semibold text-primary-dark dark:text-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>
                                {d}
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="select-none" style={{width: months * MONTH_WIDTH + (months - 1) * MONTH_GAP}}>
            {field || <SegmentedField value={value} min={min} max={max} autoFocus={autoFocus} onEnter={onEnter}
                                      onCommit={(iso) => onSelect(iso, {fromGrid: false})}/>}

            {mode === "days" ? (
                <div className="flex" style={{gap: MONTH_GAP}}>
                    {shown.map((monthDate, i) => {
                        const label = capitalize(format(monthDate, "LLLL yyyy", {locale}));
                        return (
                            <div key={i} style={{width: MONTH_WIDTH}}>
                                <div className="flex items-center justify-between h-9 mt-4 mb-2">
                                    {i === 0 ? modeToggle(label)
                                        : <span className="px-1.5 text-[17px] font-medium text-gray-900 dark:text-gray-100">{label}</span>}
                                    {i === shown.length - 1 && navButtons}
                                </div>
                                {renderDays(monthDate)}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="flex items-center justify-between h-9 mt-4 mb-2">
                    {modeToggle(modeLabel)}
                    {navButtons}
                </div>
            )}

            {mode === "months" && (
                <div className="grid grid-cols-3 gap-2 py-1">
                    {Array.from({length: 12}, (_, m) => (
                        <button key={m} type="button" disabled={monthDisabled(year, m)}
                                onClick={() => { setView(new Date(year, m, 1)); setMode("days"); }}
                                className={`h-11 rounded-lg text-sm capitalize ${m === month ? "bg-primary text-white" : "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"} disabled:text-gray-300 dark:disabled:text-gray-600 disabled:bg-transparent`}>
                            {format(new Date(year, m, 1), "LLL", {locale})}
                        </button>
                    ))}
                </div>
            )}

            {mode === "years" && (
                <div className="grid grid-cols-3 gap-2 py-1">
                    {Array.from({length: 12}, (_, i) => yearsStart + i).map((y) => (
                        <button key={y} type="button" disabled={yearDisabled(y)}
                                onClick={() => { setView(new Date(y, month, 1)); setMode("months"); }}
                                className={`h-11 rounded-lg text-sm ${y === year ? "bg-primary text-white" : "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"} disabled:text-gray-300 dark:disabled:text-gray-600 disabled:bg-transparent`}>
                            {y}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const POPOVER_WIDTH = calendarPopoverWidth(1);

/**
 * Fixed-position popover anchored to `anchorRef`, portaled to <body> so it
 * escapes modals with overflow hidden. Closes on outside click and Escape.
 */
export const CalendarPopover = ({anchorRef, onClose, align = "start", width = POPOVER_WIDTH, children}) => {
    const [pos, setPos] = useState(null);
    const popoverRef = useRef(null);

    const place = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        const height = popoverRef.current?.offsetHeight || 400;
        const below = window.innerHeight - rect.bottom;
        const top = below < height + 12 && rect.top > below ? Math.max(8, rect.top - height - 6) : rect.bottom + 6;
        const wanted = align === "center" ? rect.left + rect.width / 2 - width / 2 : rect.left;
        setPos({top, left: Math.min(Math.max(8, wanted), window.innerWidth - width - 8)});
    }, [anchorRef, align, width]);

    useLayoutEffect(() => {
        place();
        const onDown = (e) => {
            if (!popoverRef.current?.contains(e.target) && !anchorRef.current?.contains(e.target)) onClose();
        };
        const onKey = (e) => {
            if (e.key === "Escape") { e.stopPropagation(); onClose(); }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey, true);
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey, true);
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [anchorRef, onClose, place]);

    return createPortal(
        <div
            ref={popoverRef}
            role="dialog"
            style={{position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width, zIndex: 10000}}
            className="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl"
        >
            {children}
        </div>,
        document.body
    );
};

/**
 * Drop-in replacement for <input type="date">.
 * `value` is "YYYY-MM-DD" (or ""), and `onChange` receives an event-like
 * object `{target: {name, id, value}}` so existing handlers keep working.
 */
const DateInput = ({
                       value = "", onChange, name, id, min, max, disabled = false, required = false,
                       className = "", placeholder, clearable = !required, "aria-label": ariaLabel, title,
                   }) => {
    const {t} = useTranslation();
    const locale = useDateFnsLocale();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const date = parseValue(value);

    const emit = (next) => {
        const target = {name, id, value: next};
        onChange?.({target, currentTarget: target, preventDefault() {}, stopPropagation() {}});
    };

    const close = useCallback(() => { setOpen(false); triggerRef.current?.focus(); }, []);

    return (
        <>
            <button
                    ref={triggerRef}
                    type="button"
                    id={id}
                    name={name}
                    disabled={disabled}
                    aria-label={ariaLabel}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    title={title}
                    onClick={() => setOpen((o) => !o)}
                    className={`${className} flex items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60`}
                >
                    <CalendarDays className="w-4 h-4 shrink-0 text-gray-400"/>
                    <span className={`flex-1 min-w-0 truncate ${date ? "" : "text-gray-400"}`}>
                        {date ? format(date, "d MMM yyyy", {locale}) : (placeholder || t("datePicker.placeholder", "Choisir une date"))}
                    </span>
                    {clearable && date && !disabled ? (
                        <span role="button" tabIndex={-1} aria-label={t("datePicker.clear", "Effacer")}
                              onClick={(e) => { e.stopPropagation(); emit(""); }}
                              className="shrink-0 p-0.5 -mr-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <X className="w-3.5 h-3.5"/>
                        </span>
                    ) : (
                        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}/>
                    )}
            </button>
            {open && (
                <CalendarPopover anchorRef={triggerRef} onClose={close}>
                    <DateCalendar value={value} min={min} max={max} autoFocus onEnter={close}
                                  onSelect={(iso, {fromGrid}) => { emit(iso); if (fromGrid) close(); }}/>
                </CalendarPopover>
            )}
        </>
    );
};

export default DateInput;
